import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { getMessagesByChatId, createMessage } from '../apis/messageApi';
import { Message, CreateMessageRequest } from '../apis/types';

interface UseMessagesReturn {
  messages: Message[];
  loading: boolean;
  error: string | null;
  sendMessage: (data: CreateMessageRequest) => Promise<Message | null>;
}

export const useMessages = (chatId: string | null): UseMessagesReturn => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // Query for fetching messages
  const {
    data: messages = [],
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['messages', chatId],
    queryFn: async () => {
      if (!chatId) return [];
      const token = await getAccessTokenSilently();
      return getMessagesByChatId(chatId, token);
    },
    enabled: !!chatId, // Only run query if chatId exists
    staleTime: 1 * 60 * 1000, // 1 minute (messages change more frequently)
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation for sending messages
  const sendMessageMutation = useMutation({
    mutationFn: async (data: CreateMessageRequest) => {
      if (!chatId) {
        throw new Error('No chat selected');
      }
      const token = await getAccessTokenSilently();
      return createMessage(chatId, data, token);
    },
    onSuccess: response => {
      // Update the messages cache with the new messages
      queryClient.setQueryData(
        ['messages', chatId],
        (oldMessages: Message[] = []) => {
          const newMessages = [...oldMessages];
          if (response.assistantMessage) {
            newMessages.push(response.userMessage, response.assistantMessage);
          }
          return newMessages;
        }
      );

      // If the assistant message has an attachment but no bookingGenerationJobId yet,
      // the server may still be creating the booking job. Schedule a delayed
      // refetch to pick up the bookingGenerationJobId from the DB.
      if (
        response.assistantMessage?.attachment &&
        !response.assistantMessage?.bookingGenerationJobId
      ) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['messages', chatId] });
        }, 2000);
      }
    },
    onError: error => {
      console.error('Error sending message:', error);
    },
  });

  const sendMessage = async (
    data: CreateMessageRequest
  ): Promise<Message | null> => {
    try {
      const response = await sendMessageMutation.mutateAsync(data);
      return response.userMessage;
    } catch (error) {
      return null;
    }
  };

  const error =
    queryError?.message || sendMessageMutation.error?.message || null;

  return {
    messages,
    loading,
    error,
    sendMessage,
  };
};
